import api from './api';

class SettingsService {
  // Get all settings
  async getSettings(params = {}) {
    try {
      const response = await api.get('/settings', { params });
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Settings endpoint not implemented, returning mock data');
        return this.getMockSettings();
      }
      throw this.handleError(error);
    }
  }

  // Update setting
  async updateSetting(settingKey, settingValue) {
    try {
      const response = await api.put(`/settings/${settingKey}`, { setting_value: settingValue });
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('Update setting endpoint not implemented, simulating success');
        return { success: true, message: 'Setting updated successfully (simulated)' };
      }
      throw this.handleError(error);
    }
  }

  // Update multiple settings
  async updateSettings(settings) {
    try {
      const response = await api.post('/settings/bulk', settings);
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('Bulk update settings endpoint not implemented, simulating success');
        return { success: true, message: 'Settings updated successfully (simulated)' };
      }
      throw this.handleError(error);
    }
  }

  // Test email settings
  async testEmailSettings(emailSettings) {
    try {
      const response = await api.post('/settings/test-email', emailSettings);
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('Test email endpoint not implemented, simulating success');
        return { success: true, message: 'Email settings test successful (simulated)' };
      }
      throw this.handleError(error);
    }
  }

  // Create backup
  async createBackup() {
    try {
      const response = await api.post('/settings/backup');
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('Backup endpoint not implemented, simulating success');
        return { 
          success: true, 
          message: 'Backup created successfully (simulated)',
          backup_id: 'backup_' + Date.now(),
          filename: `mkm_tour_backup_${new Date().toISOString().split('T')[0]}.zip`
        };
      }
      throw this.handleError(error);
    }
  }

  // Restore backup
  async restoreBackup(backupId) {
    try {
      const response = await api.post(`/settings/restore/${backupId}`);
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('Restore backup endpoint not implemented, simulating success');
        return { success: true, message: 'Backup restored successfully (simulated)' };
      }
      throw this.handleError(error);
    }
  }

  // Get backups list
  async getBackups() {
    try {
      const response = await api.get('/settings/backups');
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning mock data
      if (error.response?.status === 501) {
        console.warn('Backups list endpoint not implemented, returning mock data');
        return this.getMockBackups();
      }
      throw this.handleError(error);
    }
  }

  // Upload logo/favicon
  async uploadFile(type, file) {
    try {
      const formData = new FormData();
      formData.append(type, file);

      const response = await api.post(`/settings/upload/${type}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error) {
      // Handle 501 (not implemented) by returning success message
      if (error.response?.status === 501) {
        console.warn('File upload endpoint not implemented, simulating success');
        return { 
          success: true, 
          message: 'File uploaded successfully (simulated)',
          file_url: URL.createObjectURL(file)
        };
      }
      throw this.handleError(error);
    }
  }

  // Mock data methods for when endpoints are not implemented
  getMockSettings() {
    return {
      settings: {
        // General Settings
        site_name: 'MKM Tour',
        site_description: 'Premier Umrah and Hajj Tour Operator',
        site_logo: '',
        favicon: '',
        timezone: 'Asia/Kuala_Lumpur',
        language: 'en',
        currency: 'MYR',
        date_format: 'DD/MM/YYYY',
        time_format: '24h',
        
        // Company Settings
        company_name: 'MKM Tour & Travel Sdn Bhd',
        registration_number: '123456789',
        address: 'Kuala Lumpur, Malaysia',
        phone: '+60 3-1234 5678',
        email: 'info@mkmtour.com',
        website: 'https://mkmtour.com',
        
        // Social Media
        facebook: '',
        twitter: '',
        instagram: '',
        youtube: '',
        
        // Email Settings
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_encryption: 'tls',
        email_from_name: 'MKM Tour',
        email_from_address: 'noreply@mkmtour.com',
        
        // Payment Settings
        payment_gateway: 'tng',
        tng_merchant_id: '',
        tng_secret_key: '',
        tng_sandbox: true,
        payment_currency: 'MYR',
        
        // Booking Settings
        booking_prefix: 'MKM',
        booking_expiry_hours: 24,
        min_booking_days: 7,
        max_booking_days: 365,
        require_passport: true,
        require_visa: false,
        
        // Notification Settings
        email_notifications: true,
        sms_notifications: false,
        admin_notifications: true,
        booking_notifications: true,
        payment_notifications: true,
        
        // System Settings
        maintenance_mode: false,
        debug_mode: false,
        cache_enabled: true,
        session_timeout: 120,
        max_file_size: 10485760, // 10MB
        allowed_file_types: 'jpg,jpeg,png,gif,pdf,doc,docx',
        
        // SEO Settings
        meta_title: 'MKM Tour - Umrah and Hajj Packages',
        meta_description: 'Book your spiritual journey with MKM Tour. Premium Umrah and Hajj packages.',
        meta_keywords: 'umrah, hajj, pilgrimage, mecca, medina, islamic tour',
        google_analytics: '',
        facebook_pixel: ''
      }
    };
  }

  getMockBackups() {
    return {
      backups: [
        {
          id: 'backup_001',
          filename: 'mkm_tour_backup_2024-01-15.zip',
          size: '45.2 MB',
          created_at: '2024-01-15T10:30:00Z',
          status: 'completed'
        },
        {
          id: 'backup_002',
          filename: 'mkm_tour_backup_2024-01-10.zip',
          size: '43.8 MB',
          created_at: '2024-01-10T10:30:00Z',
          status: 'completed'
        },
        {
          id: 'backup_003',
          filename: 'mkm_tour_backup_2024-01-05.zip',
          size: '42.1 MB',
          created_at: '2024-01-05T10:30:00Z',
          status: 'completed'
        }
      ]
    };
  }

  // Get setting categories for organization
  getSettingCategories() {
    return [
      { value: 'general', label: 'General', icon: 'Settings' },
      { value: 'company', label: 'Company', icon: 'Business' },
      { value: 'email', label: 'Email', icon: 'Email' },
      { value: 'payment', label: 'Payment', icon: 'Payment' },
      { value: 'booking', label: 'Booking', icon: 'Schedule' },
      { value: 'notification', label: 'Notifications', icon: 'Notifications' },
      { value: 'system', label: 'System', icon: 'Code' },
      { value: 'seo', label: 'SEO', icon: 'Search' },
      { value: 'backup', label: 'Backup', icon: 'Backup' }
    ];
  }

  // Get timezone options
  getTimezoneOptions() {
    return [
      { value: 'Asia/Kuala_Lumpur', label: 'Malaysia (GMT+8)' },
      { value: 'Asia/Riyadh', label: 'Saudi Arabia (GMT+3)' },
      { value: 'Asia/Dubai', label: 'UAE (GMT+4)' },
      { value: 'UTC', label: 'UTC (GMT+0)' }
    ];
  }

  // Get language options
  getLanguageOptions() {
    return [
      { value: 'en', label: 'English' },
      { value: 'ms', label: 'Bahasa Malaysia' },
      { value: 'ar', label: 'العربية' }
    ];
  }

  // Get currency options
  getCurrencyOptions() {
    return [
      { value: 'MYR', label: 'Malaysian Ringgit (MYR)' },
      { value: 'USD', label: 'US Dollar (USD)' },
      { value: 'SAR', label: 'Saudi Riyal (SAR)' },
      { value: 'AED', label: 'UAE Dirham (AED)' }
    ];
  }

  // Handle API errors
  handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    } else if (error.message) {
      return new Error(error.message);
    } else {
      return new Error('An unexpected error occurred');
    }
  }
}

export default new SettingsService();